import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Search,
    CheckCircle,
    Clock,
    XCircle,
    RefreshCw,
    Repeat,
    MessageSquare,
    AlertTriangle,
    Megaphone,
    TrendingUp,
    TrendingDown
} from 'lucide-react';

const AdminP2PTradesPage = () => {
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'ads'

    // Orders state
    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [orderSearchTerm, setOrderSearchTerm] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

    // Ads state
    const [ads, setAds] = useState([]);
    const [adsLoading, setAdsLoading] = useState(true);
    const [adSearchTerm, setAdSearchTerm] = useState('');
    const [adStatusFilter, setAdStatusFilter] = useState('');
    const [adTypeFilter, setAdTypeFilter] = useState('');

    useEffect(() => {
        if (activeTab === 'orders') {
            fetchOrders();
        } else {
            fetchAds();
        }
    }, [activeTab, orderStatusFilter, adStatusFilter, adTypeFilter]);

    const fetchOrders = async () => {
        try {
            setOrdersLoading(true);
            const params = new URLSearchParams();
            if (orderStatusFilter) params.append('status', orderStatusFilter);
            params.append('limit', pagination.limit);
            params.append('offset', pagination.offset);

            const res = await api.get(`/admin/p2p-orders?${params.toString()}`);
            setOrders(res.data.orders || []);
            setPagination(res.data.pagination || pagination);
        } catch (error) {
            console.error('Fetch P2P orders error:', error);
            toast.error('Failed to load P2P orders');
        } finally {
            setOrdersLoading(false);
        }
    };

    const fetchAds = async () => {
        try {
            setAdsLoading(true);
            const params = new URLSearchParams();
            if (adStatusFilter) params.append('status', adStatusFilter);
            if (adTypeFilter) params.append('type', adTypeFilter);

            const res = await api.get(`/admin/p2p-ads?${params.toString()}`);
            setAds(res.data.ads || []);
        } catch (error) {
            console.error('Fetch P2P ads error:', error);
            toast.error('Failed to load P2P ads');
        } finally {
            setAdsLoading(false);
        }
    };

    const getOrderStatusBadge = (status) => {
        const styles = {
            completed: 'bg-green-500/20 text-green-400 border-green-500/30',
            created: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            paid: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
            appealed: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        const icons = {
            completed: <CheckCircle size={14} />,
            created: <Clock size={14} />,
            paid: <MessageSquare size={14} />,
            cancelled: <XCircle size={14} />,
            appealed: <AlertTriangle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.created}`}>
                {icons[status] || icons.created}
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const getAdStatusBadge = (status) => {
        const styles = {
            active: 'bg-green-500/20 text-green-400 border-green-500/30',
            offline: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.active}`}>
                {status === 'active' && <CheckCircle size={14} />}
                {status === 'cancelled' && <XCircle size={14} />}
                {status === 'offline' && <Clock size={14} />}
                {status?.charAt(0).toUpperCase() + status?.slice(1)}
            </span>
        );
    };

    const getTypeBadge = (type) => {
        return type === 'sell' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30">
                <TrendingDown size={14} />
                SELL
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-green-500/20 text-green-400 border-green-500/30">
                <TrendingUp size={14} />
                BUY
            </span>
        );
    };

    const filteredOrders = orders.filter(o => {
        if (!orderSearchTerm) return true;
        const search = orderSearchTerm.toLowerCase();
        return (
            o.buyer?.email?.toLowerCase().includes(search) ||
            o.seller?.email?.toLowerCase().includes(search) ||
            o.id?.toLowerCase().includes(search)
        );
    });

    const filteredAds = ads.filter(ad => {
        if (!adSearchTerm) return true;
        const search = adSearchTerm.toLowerCase();
        return (
            ad.user?.email?.toLowerCase().includes(search) ||
            ad.user?.firstName?.toLowerCase().includes(search) ||
            ad.asset?.toLowerCase().includes(search) ||
            ad.id?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Repeat className="text-purple-400" /> P2P Trading
                        </h1>
                        <p className="text-gray-400 text-sm">Manage P2P orders and advertisements</p>
                    </div>
                    <button
                        onClick={() => activeTab === 'orders' ? fetchOrders() : fetchAds()}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex gap-1">
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`px-6 py-3 rounded-t-lg font-medium text-sm transition-all ${activeTab === 'orders'
                                ? 'bg-slate-900 text-white border border-slate-700 border-b-0'
                                : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <Repeat size={16} />
                            Orders
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('ads')}
                        className={`px-6 py-3 rounded-t-lg font-medium text-sm transition-all ${activeTab === 'ads'
                                ? 'bg-slate-900 text-white border border-slate-700 border-b-0'
                                : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <Megaphone size={16} />
                            Advertisements
                        </span>
                    </button>
                </div>
            </div>

            {activeTab === 'orders' ? (
                <>
                    {/* Orders Filters */}
                    <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                        <div className="flex flex-wrap gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by buyer/seller email or order ID..."
                                    value={orderSearchTerm}
                                    onChange={(e) => setOrderSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <select
                                value={orderStatusFilter}
                                onChange={(e) => setOrderStatusFilter(e.target.value)}
                                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="created">Created</option>
                                <option value="paid">Paid</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="appealed">Appealed</option>
                            </select>
                        </div>
                    </div>

                    {/* Orders Content */}
                    <div className="p-6">
                        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                            {ordersLoading ? (
                                <div className="text-center py-12 text-gray-400">Loading P2P orders...</div>
                            ) : filteredOrders.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <Repeat size={48} className="mx-auto mb-4 text-purple-400/50" />
                                    <p className="text-lg font-medium">No P2P orders found</p>
                                    <p className="text-sm mt-2">P2P orders will appear here when users trade</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-900/50">
                                            <tr className="text-left text-gray-400 text-sm">
                                                <th className="px-4 py-3 font-medium">Order ID</th>
                                                <th className="px-4 py-3 font-medium">Buyer</th>
                                                <th className="px-4 py-3 font-medium">Seller</th>
                                                <th className="px-4 py-3 font-medium">Asset</th>
                                                <th className="px-4 py-3 font-medium">Amount</th>
                                                <th className="px-4 py-3 font-medium">Price</th>
                                                <th className="px-4 py-3 font-medium">Total</th>
                                                <th className="px-4 py-3 font-medium">Payment</th>
                                                <th className="px-4 py-3 font-medium">Status</th>
                                                <th className="px-4 py-3 font-medium">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700">
                                            {filteredOrders.map((order) => (
                                                <tr key={order.id} className="hover:bg-slate-700/30 transition">
                                                    <td className="px-4 py-3">
                                                        <span className="text-blue-400 text-xs font-mono">
                                                            {order.id?.slice(0, 8)}...
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div>
                                                            <div className="text-white font-medium text-sm">{order.buyer?.email || 'Unknown'}</div>
                                                            <div className="text-xs text-gray-400">
                                                                {order.buyer?.firstName} {order.buyer?.lastName}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div>
                                                            <div className="text-white font-medium text-sm">{order.seller?.email || 'Unknown'}</div>
                                                            <div className="text-xs text-gray-400">
                                                                {order.seller?.firstName} {order.seller?.lastName}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-1 bg-slate-700 rounded text-white text-sm font-medium">
                                                            {order.P2PAd?.asset || 'USDT'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-white font-mono text-sm">
                                                        {parseFloat(order.amount).toFixed(4)}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-300 font-mono text-sm">
                                                        ₹{parseFloat(order.price).toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-green-400 font-mono text-sm font-medium">
                                                        ₹{parseFloat(order.fiatAmount).toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-300 text-sm">
                                                        {order.paymentMethod}
                                                    </td>
                                                    <td className="px-4 py-3">{getOrderStatusBadge(order.status)}</td>
                                                    <td className="px-4 py-3 text-gray-400 text-sm">
                                                        {new Date(order.createdAt).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 text-center text-gray-400 text-sm">
                            Showing {filteredOrders.length} of {pagination.total} orders
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Ads Filters */}
                    <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                        <div className="flex flex-wrap gap-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder="Search by user email, name, or asset..."
                                    value={adSearchTerm}
                                    onChange={(e) => setAdSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <select
                                value={adTypeFilter}
                                onChange={(e) => setAdTypeFilter(e.target.value)}
                                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">All Types</option>
                                <option value="buy">Buy</option>
                                <option value="sell">Sell</option>
                            </select>
                            <select
                                value={adStatusFilter}
                                onChange={(e) => setAdStatusFilter(e.target.value)}
                                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="offline">Offline</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>

                    {/* Ads Content */}
                    <div className="p-6">
                        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                            {adsLoading ? (
                                <div className="text-center py-12 text-gray-400">Loading P2P ads...</div>
                            ) : filteredAds.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <Megaphone size={48} className="mx-auto mb-4 text-orange-400/50" />
                                    <p className="text-lg font-medium">No P2P ads found</p>
                                    <p className="text-sm mt-2">Advertisements will appear here when users post them</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-slate-900/50">
                                            <tr className="text-left text-gray-400 text-sm">
                                                <th className="px-4 py-3 font-medium">Ad ID</th>
                                                <th className="px-4 py-3 font-medium">User</th>
                                                <th className="px-4 py-3 font-medium">Type</th>
                                                <th className="px-4 py-3 font-medium">Asset</th>
                                                <th className="px-4 py-3 font-medium">Price</th>
                                                <th className="px-4 py-3 font-medium">Amount</th>
                                                <th className="px-4 py-3 font-medium">Limits</th>
                                                <th className="px-4 py-3 font-medium">Status</th>
                                                <th className="px-4 py-3 font-medium">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-700">
                                            {filteredAds.map((ad) => (
                                                <tr key={ad.id} className="hover:bg-slate-700/30 transition">
                                                    <td className="px-4 py-3">
                                                        <span className="text-blue-400 text-xs font-mono">
                                                            {ad.id?.slice(0, 8)}...
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div>
                                                            <div className="text-white font-medium text-sm">
                                                                {ad.user?.firstName} {ad.user?.lastName}
                                                            </div>
                                                            <div className="text-xs text-gray-400">{ad.user?.email}</div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">{getTypeBadge(ad.type)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-1 bg-slate-700 rounded text-white text-sm font-medium">
                                                            {ad.asset}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-white font-mono text-sm">
                                                        ₹{parseFloat(ad.price).toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-white text-sm">
                                                            {parseFloat(ad.remainingAmount).toFixed(4)}
                                                        </div>
                                                        <div className="text-xs text-gray-400">
                                                            of {parseFloat(ad.totalAmount).toFixed(4)}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-300 text-sm">
                                                        ₹{parseFloat(ad.minLimit).toFixed(0)} - ₹{parseFloat(ad.maxLimit).toFixed(0)}
                                                    </td>
                                                    <td className="px-4 py-3">{getAdStatusBadge(ad.status)}</td>
                                                    <td className="px-4 py-3 text-gray-400 text-sm">
                                                        {new Date(ad.createdAt).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 text-center text-gray-400 text-sm">
                            Showing {filteredAds.length} advertisements
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminP2PTradesPage;
